import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Invitation, ReceivedInvitation } from '../domain/invitations';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import ConfirmDialog from '../components/ConfirmDialog';
import { ActionError, StateForError } from '../components/PageStates';
import UserAvatar from '../components/UserAvatar';
import { fullName } from '../utils/people';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, ChevronRight, Copy } from 'lucide-react';

/**
 * `/invitations` — personal invites waiting for me, and the invites I've
 * made that are still active (014 R16). Each section loads on its own, so one
 * failing leaves the other usable.
 */
export default function InvitationsPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-12">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">Invitations</h1>
      <WaitingForYou />
      <YourInvites />
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-4">
      <h2 id={id} className="text-2xl font-bold text-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function RowsLoading({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-3">
      <Skeleton className="h-20 rounded-3xl" />
      <Skeleton className="h-20 rounded-3xl" />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-3xl bg-card px-6 py-8 text-muted-foreground">{children}</p>;
}

// ── Waiting for you ────────────────────────────────────────────────────

function WaitingForYou() {
  const { invitations } = useRepositories();
  const received = useLoad<ReceivedInvitation[]>(() => invitations.received(), 'received');

  let body: React.ReactNode;
  if (received.status === 'loading') body = <RowsLoading label="Loading invites for you" />;
  else if (received.status === 'error')
    body = <StateForError error={received.error} onRetry={received.reload} />;
  else if (received.data.length === 0) body = <Empty>No invites waiting for you.</Empty>;
  else
    body = (
      <ul className="space-y-3">
        {received.data.map((r) => (
          <li key={r.invitationId}>
            <ReceivedRow invite={r} />
          </li>
        ))}
      </ul>
    );

  return (
    <Section id="invitations-waiting" title="Waiting for you">
      {body}
    </Section>
  );
}

function ReceivedRow({ invite }: { invite: ReceivedInvitation }) {
  const inviter =
    invite.inviter.kind === 'person'
      ? invite.inviter.person
      : { firstName: invite.inviter.firstName, lastName: `${invite.inviter.lastInitial}.` };
  return (
    <Link
      to={`/invitations/received/${invite.invitationId}`}
      className="flex items-center gap-4 rounded-3xl bg-card p-5 transition-colors hover:bg-secondary"
    >
      <UserAvatar
        person={{ avatarUrl: null, ...inviter }}
        className="size-12"
        textClassName="text-base"
        labelled
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-foreground">{fullName(inviter)}</p>
        <p className="text-sm text-muted-foreground">
          {invite.offers.length === 0
            ? 'Invites you to connect'
            : invite.offers.map((o, i) => (
                <React.Fragment key={o.offerId}>
                  {i > 0 && ', '}
                  <span>{o.collectionName}</span>
                </React.Fragment>
              ))}
        </p>
      </div>
      <ChevronRight className="size-5 text-muted-foreground" />
    </Link>
  );
}

// ── Your invites ───────────────────────────────────────────────────────

function YourInvites() {
  const { invitations } = useRepositories();
  const mine = useLoad<Invitation[]>(() => invitations.list(), 'mine');
  const [rows, setRows] = useState<Invitation[]>([]);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (mine.status === 'ready') setRows(mine.data);
  }, [mine.status, mine.data]);

  const turnOff = async (id: string) => {
    setError(null);
    try {
      await invitations.revoke(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setError(e);
    }
  };

  let body: React.ReactNode;
  if (mine.status === 'loading') body = <RowsLoading label="Loading your invites" />;
  else if (mine.status === 'error')
    body = <StateForError error={mine.error} onRetry={mine.reload} />;
  else if (rows.length === 0) body = <Empty>You have no active invites.</Empty>;
  else
    body = (
      <ul className="space-y-3">
        {rows.map((invitation) => (
          <InviteRow key={invitation.id} invitation={invitation} onTurnOff={turnOff} />
        ))}
      </ul>
    );

  return (
    <Section id="invitations-yours" title="Your invites">
      {error !== null && <ActionError error={error} />}
      {body}
    </Section>
  );
}

function inviteName(invitation: Invitation): string {
  if (invitation.label) return invitation.label;
  if (invitation.invitee) return `To ${fullName(invitation.invitee)}`;
  return 'Group link';
}

function usesText({ useCount, maxUses }: Invitation): string {
  if (maxUses === null) return `${useCount} ${useCount === 1 ? 'use' : 'uses'}`;
  return `${useCount} of ${maxUses} ${maxUses === 1 ? 'use' : 'uses'}`;
}

function expiryText(expiresAt: string | null): string {
  if (!expiresAt) return 'No expiry';
  const date = new Date(expiresAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return `Expires ${date}`;
}

function InviteRow({
  invitation,
  onTurnOff,
}: {
  invitation: Invitation;
  onTurnOff: (id: string) => Promise<void>;
}) {
  const { invitations } = useRepositories();
  const [copy, setCopy] = useState<'idle' | 'copying' | 'copied' | 'failed'>('idle');
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [copyError, setCopyError] = useState<unknown>(null);
  const name = inviteName(invitation);

  const copyLink = async () => {
    setCopy('copying');
    setCopyError(null);
    let link: string;
    try {
      link = (await invitations.newLink(invitation.id)).link;
    } catch (e) {
      setCopyError(e);
      setCopy('idle');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopy('copied');
    } catch {
      // The clipboard can be refused; show the link to copy by hand.
      setFallbackLink(link);
      setCopy('failed');
    }
  };

  return (
    <li aria-label={name} className="space-y-3 rounded-3xl bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-foreground">{name}</p>
          <p className="text-sm text-muted-foreground">
            {invitation.offers.length === 0
              ? 'Connects you'
              : invitation.offers.map((o, i) => (
                  <React.Fragment key={o.id}>
                    {i > 0 && ', '}
                    <span className={o.collection ? undefined : 'italic'}>
                      {o.collection ? o.collection.name : 'Removed'}
                    </span>
                  </React.Fragment>
                ))}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            onClick={copyLink}
            disabled={copy === 'copying'}
          >
            {copy === 'copied' ? <Check /> : <Copy />}
            {copy === 'copied' ? 'Copied' : 'Copy link'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => setConfirming(true)}
          >
            Turn off
          </Button>
        </div>
      </div>

      <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span>{usesText(invitation)}</span>
        <span>{expiryText(invitation.expiresAt)}</span>
      </p>

      {invitation.acceptedBy.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {invitation.acceptedBy.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-2 text-sm text-foreground">
              <UserAvatar person={p} className="size-6" textClassName="text-[10px]" />
              {fullName(p)}
            </span>
          ))}
        </div>
      )}

      {fallbackLink && (
        <p className="break-all rounded-2xl bg-muted px-4 py-2 font-mono text-xs">
          Couldn't copy — here's the link: {fallbackLink}
        </p>
      )}
      {copyError !== null && <ActionError error={copyError} />}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Turn off this invite?"
        description="Nobody will be able to use its links any more. People who already joined keep what they have. This can't be undone."
        confirmLabel="Turn off"
        onConfirm={() => onTurnOff(invitation.id)}
      />
    </li>
  );
}
